import { getHostedFileUrl } from '~kui/utils/file';

import { useIaPlaces } from '~/composables/unified/useIaPlaces';
import { SEARCH_FETCH_ALL_INDUSTRY_HUBS } from '~/graphql/documents/search';

export interface IndustryHubCard {
  slug: string;
  title: string;
  heroImageUrl: string | null;
  to: string;
}

/**
 * The hub cards the /career-advice index lists once the hubs-enabled flag is on, served from the
 * consideration-areas index. Fetched during SSR so the cards are crawlable links in the server
 * HTML; the backend answers an index failure with an empty list, which the grid shows as its
 * empty state rather than failing the page.
 */
export const useIndustryHubCards = async ({ lazy = true }: { lazy?: boolean } = {}) => {
  const { $urqlClient } = useNuxtApp();
  const places = useIaPlaces();

  // `lazy` keeps the server fetch blocking while a client-side navigation paints the page and the
  // grid's skeleton straight away; a caller with its own skeleton passes `lazy: false` to wait.
  const { data, pending: fetching } = await useAsyncData(
    'career-advice:industry-hubs',
    async () => {
      const result = await $urqlClient.query(SEARCH_FETCH_ALL_INDUSTRY_HUBS, {}).toPromise();

      if (result.error) {
        throw result.error;
      }

      return result.data?.search?.fetchAllIndustryHubs ?? [];
    },
    { server: true, lazy },
  );

  // Resolved here rather than in the fetcher: `getHostedFileUrl` reads the Nuxt context, which is
  // gone after the first `await` inside an asyncData handler.
  const hubs = computed<IndustryHubCard[]>(() =>
    (data.value ?? []).map(({ slug, title, heroImagePath }) => ({
      slug,
      title,
      heroImageUrl: heroImagePath ? getHostedFileUrl(heroImagePath) : null,
      to: places.hub(slug),
    })),
  );

  return { hubs, fetching };
};
